import Swal from 'sweetalert2';
import { stampPoseImageRecordTypes } from '../../config/persistMigrations';
import { stampPosesFromGroupPhotoIds } from '../../utils/poseImageRecordTypes';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { push } from 'connected-react-router';
import { setSmsPhone } from '../action_parent';

import { mapCartTotalsResponseToCamelCase } from '../../utils/api';
import cartUtils from '../../utils/cart';
import { selectIncentiveConfiguration } from '../../selectors';
import escapeHtml from '../../utils/escapeHtml';
import unconfiguredBundlesAlert from './unconfiguredBundlesAlert';

// Restore requests in flight, keyed by the store's getState. While one is
// pending, storeCart and submitCart do nothing, so the device's old cart is
// never persisted over the saved cart that is about to replace it. Not kept
// in Redux: it is network coordination, not state to persist.
const cartRecoveryRequests = new WeakMap();

export const V3_POP_ITEM_FROM_CONFIG = 'V3_POP_ITEM_FROM_CONFIG';
export const V3_SET_PRODUCT_CONFIG = 'V3_SET_PRODUCT_CONFIG';
export const V3_CLEAR_PRODUCT_CONFIG = 'V3_CLEAR_PRODUCT_CONFIG';
export const V3_ADD_TO_CART = 'V3_ADD_TO_CART';
export const V3_DECLINE_INTERSTITIAL_PRODUCT =
  'V3_DECLINE_INTERSTITIAL_PRODUCT';
export const V3_DECLINE_PRE_CART_OFFER_PRODUCT =
  'V3_DECLINE_PRE_CART_OFFER_PRODUCT';
export const V3_DECLINE_RETOUCHING = 'V3_DECLINE_RETOUCHING';
export const V3_DECLINE_PERSONALIZATION = 'V3_DECLINE_PERSONALIZATION';
export const V3_SUBMIT_PERSONALIZATION = 'V3_SUBMIT_PERSONALIZATION';
export const V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS =
  'V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS';
export const V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS =
  'V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS';
export const V3_STORE_CART_SUCCESS = 'V3_STORE_CART_SUCCESS';
export const V3_STORE_CART_ERROR = 'V3_STORE_CART_ERROR';
export const V3_STORE_CART = 'V3_STORE_CART';
export const V3_SUBMITTING_CART = 'V3_SUBMITTING_CART';
export const V3_SUBMIT_CART_SUCCESS = 'SUCCESS';
export const V3_SUBMIT_CART_ERROR = 'V3_SUBMIT_CART_ERROR';
export const V3_CLEAR_CART = 'V3_CLEAR_CART';
export const V3_REMOVE_FROM_CONFIG_STACK = 'V3_REMOVE_FROM_CONFIG_STACK';
export const V3_ADD_TO_CONFIG_STACK = 'V3_ADD_TO_CONFIG_STACK';
export const V3_REMOVE_INDEX_FROM_PRODUCT_CONFIG =
  'V3_REMOVE_INDEX_FROM_PRODUCT_CONFIG';
export const V3_REMOVE_CART_ITEM = 'V3_REMOVE_CART_ITEM';
export const V3_RECORD_RETOUCHING_OFFERED = 'V3_RECORD_RETOUCHING_OFFERED';
export const V3_LOG_RETOUCHING_OFFER = 'V3_LOG_RETOUCHING_OFFER';
export const V3_RESOLVE_RETOUCHING_OFFER = 'V3_RESOLVE_RETOUCHING_OFFER';
export const V3_UPDATE_RETOUCHING_SELECTION = 'V3_UPDATE_RETOUCHING_SELECTION';
export const V3_CLEAR_RETOUCHING_SELECTION = 'V3_CLEAR_RETOUCHING_SELECTION';
export const V3_UPDATE_PARENT_SMS_NUMBER = 'V3_UPDATE_PARENT_SMS_NUMBER';
export const V3_UPDATE_SMS_NUMBER_CONFIRMED = 'V3_UPDATE_SMS_NUMBER_CONFIRMED';
export const V3_REMOVE_ITEM_FROM_CONFIG = 'V3_REMOVE_ITEM_FROM_CONFIG';
export const V3_APPLY_DISCOUNT_CODE_TO_CART = 'V3_APPLY_DISCOUNT_CODE_TO_CART';
export const V3_CHECKOUT_LOADING = 'V3_CHECKOUT_LOADING';
export const V3_RESET_SUBMITTING_ORDER = 'V3_RESET_SUBMITTING_ORDER';
export const V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED =
  'V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED';
export const V3_CONFIRM_SHIPPING_ADDRESS = 'V3_CONFIRM_SHIPPING_ADDRESS';
export const V3_SET_ACCEPTED_PICS_PLUS = 'V3_SET_ACCEPTED_PICS_PLUS';
export const V3_SET_CART_UUID = 'V3_SET_CART_UUID';
export const V3_RESTORE_ABANDONED_CART_ATTRIBUTION =
  'V3_RESTORE_ABANDONED_CART_ATTRIBUTION';
export const V3_SET_OPTIONAL_INCENTIVE_TO_CONFIGURE =
  'V3_SET_OPTIONAL_INCENTIVE_TO_CONFIGURE';
export const V3_CLEAR_OPTIONAL_INCENTIVE_TO_CONFIGURE =
  'V3_CLEAR_OPTIONAL_INCENTIVE_TO_CONFIGURE';
export const V3_SKIP_OPTIONAL_INCENTIVE = 'V3_SKIP_OPTIONAL_INCENTIVE';
export const V3_ADD_INCENTIVE_TO_ORDER = 'V3_ADD_INCENTIVE_TO_ORDER';

// `sku` rides along because cart-level identity is the benefit's SKU, not a
// product id: the row the re-add resolves against may be a different price
// list's row for the same benefit (ENG-2043 R3).
export const setOptionalIncentiveToConfigure = ({
  productId,
  sku = null,
  orderKey,
}) => ({
  type: V3_SET_OPTIONAL_INCENTIVE_TO_CONFIGURE,
  payload: { productId, sku, orderKey },
});

export const clearOptionalIncentiveToConfigure = () => ({
  type: V3_CLEAR_OPTIONAL_INCENTIVE_TO_CONFIGURE,
});

export const skipOptionalIncentive = ({
  productId,
  sku = null,
  incentiveAppliesTo,
  orderKey,
}) => ({
  type: V3_SKIP_OPTIONAL_INCENTIVE,
  payload: { productId, sku, incentiveAppliesTo, orderKey },
});

// Add a bonus straight back to the order that earned it, with no configurator
// and no navigation (ENG-2043 re-add Tier 1). Only for rows that need no
// choices from the parent — cartUtils.productRequiresIncentiveConfiguration is
// what decides that, at the call site.
//
// Everything comes from the TARGET order, never from `state.shoot`: the eventId
// stamped on the cart item is the target order's own event, so the server
// resolves the right shoot for its restriction and ownership checks even when
// the parent last opened a different school's gallery.
export const addIncentiveToOrder =
  ({ orderKey, product }) =>
  (dispatch, getState) => {
    const order = getState().v3Order?.orders?.[orderKey];
    // A bonus can only ride along with something paid for. Available Bonuses
    // only ever lists entries that resolved to a real qualifying order, so
    // getting here without one means the cart changed underneath the list (or
    // an owner was resolved wrongly) — report it rather than inventing an
    // order.
    const hasPaidItem = (order?.cartItems || []).some(
      (item) => !item.isIncentive,
    );

    if (!order || !hasPaidItem) {
      Sentry.captureMessage('Unable to add optional incentive to order', {
        level: 'error',
        extra: {
          reason: order ? 'order_has_no_paid_item' : 'order_not_found',
          orderKey,
          incentiveProductId: product?.id ?? null,
        },
      });
      Swal.fire(
        'We could not add that bonus',
        'Your cart has changed. Please reload your cart and try again.',
        'error',
      );
      return null;
    }

    // The order's own stashed row carries the restriction flag of the shoot
    // that served it; the product may have come from the cross-order listing
    // union, where it is tagged the same way.
    const stashRow = [
      ...(order.orderLevelIncentiveProducts || []),
      ...(order.incentiveProducts || []),
    ].find((row) => row.id === product.id);

    const cartObject = cartUtils.configureCartItem({
      backgroundIds: [],
      backgroundSelections: [],
      incentiveProductIds: [product.id],
      productConfig: {},
      productData: product,
      poses: [],
      poseIds: [],
      personalizationValueForCart: null,
      personalizationSetToSameValue: false,
      quantity: 1,
      requiredFields: [],
      legacyRequiredFields: [],
      productOptionKey: null,
      productOptions: {},
      eventId: order.event?.id ?? null,
      selectedCropOptions: [],
      blockInternationalShipping:
        (stashRow ?? product)?.block_international_shipping === true,
    });

    return dispatch({
      type: V3_ADD_INCENTIVE_TO_ORDER,
      payload: {
        orderKey,
        cartObject,
        productId: product.id,
        sku: product.sku ?? null,
      },
    });
  };

const missingRequiredProductOptionsMessage = (missingOption = {}) => {
  const productName = escapeHtml(missingOption.product_name || 'This product');
  const optionSetName = escapeHtml(
    missingOption.option_set_name || 'a required option',
  );
  const reason = missingOption.reason;

  if (
    reason === 'invalid_product_option_selection' ||
    reason === 'invalid_selection'
  ) {
    return `${productName} has an invalid option selection for ${optionSetName}.`;
  }

  return `${productName} requires an option selection for ${optionSetName}.`;
};

const missingRequiredProductOptionsHtml = (missingOptions = []) => {
  const missingOptionsList = missingOptions
    .map(
      (missingOption) =>
        `<li>${missingRequiredProductOptionsMessage(missingOption)}</li>`,
    )
    .join('');

  const details = missingOptionsList
    ? `<ul>${missingOptionsList}</ul>`
    : '<p>One or more products in your cart require product option updates.</p>';

  return `
    <p>Some product options in your cart need attention before checkout can continue.</p>
    ${details}
    <p>Please return to your cart or product flow and update or re-add the affected item before submitting.</p>
  `;
};

export const setProductConfig =
  ({ index, itemToAdd }) =>
  (dispatch, getState) => {
    const {
      isPrepay,
      postpayRequiresSameValuePersonalization,
      prepayRequiresSameValuePersonalization,
    } = getState().shoot;

    dispatch({
      type: V3_SET_PRODUCT_CONFIG,
      payload: {
        index,
        isPrepay,
        itemToAdd,
        prepayRequiresSameValuePersonalization,
      },
    });
  };

export function popItemFromConfig(index, section) {
  return {
    type: V3_POP_ITEM_FROM_CONFIG,
    payload: { index, section },
  };
}

export function removeItemFromConfig(index, history) {
  return {
    type: V3_REMOVE_ITEM_FROM_CONFIG,
    payload: { index, history },
  };
}

export function clearProductConfig(key) {
  return {
    type: V3_CLEAR_PRODUCT_CONFIG,
    payload: key,
  };
}

export function removeIndexFromProductConfig({ toRemove, index, history }) {
  return {
    type: V3_REMOVE_INDEX_FROM_PRODUCT_CONFIG,
    payload: { toRemove, index, history },
  };
}

export function removeFromConfigStack(number) {
  return {
    type: V3_REMOVE_FROM_CONFIG_STACK,
    payload: number,
  };
}

export function addToConfigStack(paths) {
  return {
    type: V3_ADD_TO_CONFIG_STACK,
    payload: paths,
  };
}

export function declineRetouching(orderKey) {
  return {
    type: V3_DECLINE_RETOUCHING,
    payload: { orderKey },
  };
}

export function declinePersonalization(orderKey) {
  return {
    type: 'V3_DECLINE_PERSONALIZATION',
    payload: { orderKey },
  };
}

export function submitPersonalization(orderKey, value) {
  return {
    type: 'V3_SUBMIT_PERSONALIZATION',
    payload: { orderKey, value },
  };
}

export function setPersonalizationOnAllEligibleCartItems(
  orderKey,
  personalizationValue,
  redirectSource = null,
) {
  return {
    type: V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS,
    payload: { personalizationValue, redirectSource, orderKey },
  };
}

export function skipPersonalizationOnAllCartItems(
  orderKey,
  redirectSource = null,
) {
  return {
    type: V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS,
    payload: { redirectSource, orderKey },
  };
}

// Every photo a retouching screen has shown for this order, whatever the
// parent did there. The reducer unions the ids into
// order.retouching.offeredPoseIds and spends the one-way post-add prompt.
export function recordRetouchingOffered(orderKey, poseIds) {
  return {
    type: V3_RECORD_RETOUCHING_OFFERED,
    payload: { orderKey, poseIds },
  };
}

// One offer impression ({ surface, shownAt, photoCount }), logged as 'left'
// until the screen resolves it. Lives only in the cart JSON for later
// conversion analysis; nothing prices or validates off it.
export function logRetouchingOffer(orderKey, entry) {
  return {
    type: V3_LOG_RETOUCHING_OFFER,
    payload: { orderKey, entry },
  };
}

// outcome: 'added' | 'declined', set on the newest entry still 'left'.
export function resolveRetouchingOffer(orderKey, outcome) {
  return {
    type: V3_RESOLVE_RETOUCHING_OFFER,
    payload: { orderKey, outcome },
  };
}

export const applyRetouchingQuote = (payload) => ({
  type: 'V3_APPLY_RETOUCHING_QUOTE',
  payload,
});

const quotePayload = (data, state, generation) => {
  const rawTotals = {
    ...data,
    cart_totals_object: data.cart_totals || data.cart_totals_object,
  };
  const voucherItems = Array.isArray(data.voucher_purchase_items)
    ? data.voucher_purchase_items.map((row) => {
        const original = (state.cartVoucherItems?.items || []).find(
          (item) =>
            String(item.voucher_product_id) ===
              String(row.voucher_product_id) &&
            (item.order_key == null || item.order_key === row.order_key),
        );
        return {
          ...original,
          ...row,
          voucher_product: {
            ...original?.voucher_product,
            ...row.voucher_product,
          },
        };
      })
    : undefined;
  return {
    digest: data.retouchingQuoteDigest,
    required: data.retouchingQuoteRequired,
    checkoutApplicable: data.retouchingCheckoutApplicable,
    productContexts: data.retouchingProductContexts,
    cartTotals: mapCartTotalsResponseToCamelCase(rawTotals),
    generation,
    voucherItems,
    voucherSubtotalCents: data.voucher_purchase_subtotal_cents,
  };
};
const needsRetouchingReview = (order) =>
  order.retouchingCheckoutApplicable == null ||
  ((order.retouchingQuoteRequired ||
    Object.values(order.orders || {}).some((destination) =>
      (destination.cartItems || []).some((item) => item.isRetouching),
    )) &&
    order.retouchingQuoteReviewedGeneration !==
      (order.retouchingQuoteGeneration || 0));
const retireReviewedCheckout = (dispatch, getState, uuid, attemptId) => {
  const current = getState().v3Order;
  if (
    current.uuid !== uuid ||
    (current.retouchingAttemptId && current.retouchingAttemptId !== attemptId)
  )
    return false;
  dispatch({ type: 'V3_RETOUCHING_CHECKOUT_RETIRED', payload: { attemptId } });
  return true;
};

const showProtectedCheckout = (message) =>
  Swal.fire({
    icon: 'warning',
    title: 'Checkout Needs Attention',
    text:
      message ||
      'Your checkout is protected. Please retry the original payment, return from payment to cancel, or contact support.',
  });

export const storeCart = () => async (dispatch, getState) => {
  if (
    cartRecoveryRequests.has(getState) ||
    getState().v3Order.retouchingCheckoutProtected ||
    getState().v3Order.retouchingCheckoutRequest
  )
    return;
  const generation = getState().v3Order.retouchingQuoteGeneration || 0;
  const checkoutRequest = getState().v3Order.retouchingCheckoutRequest;
  const { abandonedCartNotificationId, discountCodes, orders, uuid } =
    getState().v3Order;
  const { studentsDetails } = getState().bnlPreShoot || {};
  const { items: voucherItems, voucherSubtotalCents } =
    getState().cartVoucherItems || {};

  dispatch({ type: V3_STORE_CART });

  // Retrieve the cookie value and attempt to parse it as an integer
  const rawValue = Cookies.get('campaign_event_id');
  const parsedValue = parseInt(rawValue, 10);

  const campaignEventId = isNaN(parsedValue) ? null : parsedValue;

  try {
    const response = await axios.post('/api/v3/orders/store-cart', {
      campaignEventId,
      // Only a cart restored from a reminder carries one; every other checkout
      // posts exactly the body it always did.
      ...(abandonedCartNotificationId ? { abandonedCartNotificationId } : {}),
      discountCodes,
      orders,
      studentsDetails,
      uuid,
      voucherSubtotalCents: voucherSubtotalCents || 0,
      voucherItems: voucherItems || [],
    });

    if (
      getState().v3Order.uuid !== uuid ||
      (getState().v3Order.retouchingQuoteGeneration || 0) !== generation ||
      getState().v3Order.retouchingCheckoutRequest
    ) {
      dispatch({ type: V3_STORE_CART_ERROR });
      return false;
    }
    if (response.data.retouchingProductContexts)
      dispatch(
        applyRetouchingQuote(
          quotePayload(response.data, getState(), generation),
        ),
      );
    const cartTotals = mapCartTotalsResponseToCamelCase(response.data);
    const offerPicsPlus = response.data.offer_pics_plus === true ? true : false;
    const isPicsPlusMember =
      response.data.is_pics_plus_member === true ? true : false;
    const picsPlusSubscriptionInfo =
      response.data.pics_plus_subscription_info || null;
    const subscriberCount = response.data.subscriber_count || 0;
    const picsPlusExperiment = response.data.pics_plus_experiment || null;

    dispatch({
      type: V3_STORE_CART_SUCCESS,
      payload: {
        cartTotals: getState().v3Order.retouchingQuoteError
          ? getState().v3Order.cartTotals
          : cartTotals,
        offerPicsPlus,
        isPicsPlusMember,
        picsPlusSubscriptionInfo,
        subscriberCount,
        picsPlusExperiment,
      },
    });
    return !getState().v3Order.retouchingQuoteError;
  } catch (error) {
    const current = getState().v3Order;
    if (
      current.uuid !== uuid ||
      (current.retouchingQuoteGeneration || 0) !== generation ||
      current.retouchingCheckoutRequest !== checkoutRequest ||
      current.retouchingCheckoutProtected
    )
      return false;
    dispatch({ type: V3_STORE_CART_ERROR });

    if (
      error?.response?.data?.error_identifier ===
      'cart_support_identity_changed'
    ) {
      return Swal.fire({
        icon: 'warning',
        title: 'Start a new cart',
        text: 'This cart was prepared in a different support session. Start a new cart to continue.',
        confirmButtonText: 'Start new cart',
      }).then(() => {
        dispatch({ type: V3_CLEAR_CART });
        window.location.href = '/dashboard';
      });
    }

    if (
      error?.response?.status === 422 &&
      error.response.data?.error_identifier === 'cart_requires_recovery'
    ) {
      return Swal.fire({
        icon: 'warning',
        title: 'Order Needs Support',
        text: error.response.data.message,
      });
    }

    if (
      error?.response?.data?.error_identifier === 'payment_already_submitted' &&
      (error.response.data.retouchingCheckoutApplicable === true ||
        error.response.data.retouchingAttemptId)
    ) {
      dispatch({
        type: 'V3_RETOUCHING_CHECKOUT_PENDING',
        payload: {
          request: getState().v3Order.retouchingCheckoutRequest,
          attemptId: error.response.data.retouchingAttemptId,
          message: error.response.data.message,
        },
      });
      return showProtectedCheckout(error.response.data.message);
    }
    return Swal.fire({
      icon: 'error',
      text:
        error?.response?.data?.message ||
        'There was an error loading your cart. Your cart has been preserved. Please review it or contact support.',
    });
  }
};

// TODO: clean this up
export const submitCart =
  (stripeCancelUrl, addedPicsPlus = false) =>
  async (dispatch, getState) => {
    if (cartRecoveryRequests.has(getState)) return;
    const { discountCodes, orders, shippingAddressConfirmed, uuid } =
      getState().v3Order;
    const { items: voucherItems, voucherSubtotalCents } =
      getState().cartVoucherItems || {};

    const state = getState();
    const generation = state.v3Order.retouchingQuoteGeneration || 0;
    if (
      state.v3Order.retouchingCheckoutProtected &&
      !state.v3Order.retouchingCheckoutRequest
    )
      return showProtectedCheckout(state.v3Order.retouchingCheckoutMessage);
    if (
      !state.v3Order.retouchingCheckoutRequest &&
      needsRetouchingReview(state.v3Order)
    ) {
      await dispatch(storeCart());
      dispatch({ type: V3_SUBMIT_CART_ERROR });
      return Swal.fire({
        icon: 'info',
        title: 'Review Your Cart',
        text: 'Please review the refreshed prices, then place your order again.',
      });
    }
    if (
      state.v3Order.retouchingQuoteError &&
      !state.v3Order.retouchingCheckoutRequest
    )
      return showProtectedCheckout(state.v3Order.retouchingQuoteError);
    const cancelUrl = new URL(stripeCancelUrl, window.location.origin);
    cancelUrl.searchParams.set('cart_uuid', uuid);
    cancelUrl.searchParams.delete('retouchingAttemptId');
    const request = state.v3Order.retouchingCheckoutRequest || {
      addedPicsPlus,
      discountCodes,
      orders,
      shippingAddressConfirmed,
      stripe_cancel_url: cancelUrl.href,
      uuid,
      voucherSubtotalCents: voucherSubtotalCents || 0,
      voucherItems: voucherItems || [],
      retouchingCheckoutApplicable: state.v3Order.retouchingCheckoutApplicable,
      ...(state.v3Order.retouchingQuoteDigest
        ? { retouchingQuoteDigest: state.v3Order.retouchingQuoteDigest }
        : {}),
      ...(state.v3Order.abandonedCartNotificationId
        ? {
            abandonedCartNotificationId:
              state.v3Order.abandonedCartNotificationId,
          }
        : {}),
    };
    dispatch({ type: V3_SUBMITTING_CART });
    if (
      state.v3Order.retouchingCheckoutApplicable === true ||
      state.v3Order.retouchingCheckoutRequest
    )
      dispatch({
        type: 'V3_RETOUCHING_CHECKOUT_PENDING',
        payload: { request: JSON.parse(JSON.stringify(request)) },
      });

    try {
      const response = await axios.post('/api/v3/orders/submit', request);
      if (
        getState().v3Order.uuid !== uuid ||
        (getState().v3Order.retouchingQuoteGeneration || 0) !== generation
      )
        return false;
      if (response.data.retouchingAttemptId)
        dispatch({
          type: 'V3_RETOUCHING_CHECKOUT_PENDING',
          payload: {
            attemptId: response.data.retouchingAttemptId,
            request: JSON.parse(JSON.stringify(request)),
          },
        });
      const {
        payment_required,
        message,
        no_payment_required_redirect_url,
        session_url,
      } = response.data;

      if (
        !payment_required &&
        message === 'no payment required' &&
        no_payment_required_redirect_url
      ) {
        return (window.location.href = no_payment_required_redirect_url);
      }

      if (session_url) {
        return (window.location.href = session_url);
      } else {
        dispatch({
          type: V3_SUBMIT_CART_ERROR,
          payload: 'session_url is missing in the response.',
        });
      }
    } catch (error) {
      const response = error?.response;

      const data = response?.data;
      if (
        getState().v3Order.uuid !== uuid ||
        (getState().v3Order.retouchingQuoteGeneration || 0) !== generation
      )
        return false;
      if (data?.error_identifier === 'cart_support_identity_changed') {
        return Swal.fire({
          icon: 'warning',
          title: 'Start a new cart',
          text: 'This cart was prepared in a different support session. Start a new cart to continue.',
          confirmButtonText: 'Start new cart',
        }).then(() => {
          dispatch({ type: V3_CLEAR_CART });
          window.location.href = '/dashboard';
        });
      }

      if (data?.retouchingAttemptId)
        dispatch({
          type: 'V3_RETOUCHING_CHECKOUT_PENDING',
          payload: {
            attemptId: data.retouchingAttemptId,
            request: JSON.parse(JSON.stringify(request)),
            message: data.message,
          },
        });
      if (
        response?.status === 422 &&
        data?.error_identifier === 'retouching_price_changed'
      ) {
        if (
          state.v3Order.retouchingCheckoutRequest ||
          state.v3Order.retouchingAttemptId ||
          data?.retouchingAttemptId
        ) {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data.message });
          return showProtectedCheckout(
            'The existing payment remains protected. Please cancel it explicitly before reviewing changed prices.',
          );
        }
        dispatch({ type: 'V3_RETOUCHING_CHECKOUT_NOT_ACCEPTED' });
        if (
          getState().v3Order.uuid === uuid &&
          (getState().v3Order.retouchingQuoteGeneration || 0) === generation
        )
          dispatch(
            applyRetouchingQuote(quotePayload(data, getState(), generation)),
          );
        dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data.message });
        return Swal.fire({
          icon: 'info',
          title: 'Prices Have Changed',
          text: 'Please review the updated prices and selections, then place your order again.',
        });
      }
      if (
        response?.status === 422 &&
        data?.error_identifier === 'retouching_checkout_expired' &&
        data.requiresReview &&
        data.retiredRetouchingAttemptId
      ) {
        if (
          !retireReviewedCheckout(
            dispatch,
            getState,
            uuid,
            data.retiredRetouchingAttemptId,
          )
        ) {
          dispatch({ type: V3_SUBMIT_CART_ERROR });
          return showProtectedCheckout(
            'This response belongs to an older checkout. The current payment remains protected.',
          );
        }
        await dispatch(storeCart());
        dispatch({ type: V3_SUBMIT_CART_ERROR });
        return Swal.fire({
          icon: 'info',
          text: 'The previous checkout expired. Please review your cart and place your order again.',
        });
      }
      if (
        data?.error_identifier === 'payment_already_submitted' ||
        response?.status === 503
      ) {
        dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });
        return showProtectedCheckout(data?.message);
      }
      // A definite pre-acceptance refusal is reviewable. Unknown/network errors
      // keep exact request evidence for a safe identical retry.
      if (
        response?.status === 422 &&
        !state.v3Order.retouchingCheckoutRequest &&
        !data?.retouchingAttemptId
      )
        dispatch({ type: 'V3_RETOUCHING_CHECKOUT_NOT_ACCEPTED' });
      // Handle different 422 error scenarios
      if (response?.status === 422) {
        const { data } = response;

        if (data?.error_identifier === 'cart_requires_recovery') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data.message });
          return Swal.fire({
            icon: 'warning',
            title: 'Order Needs Support',
            text: data.message,
          });
        }

        if (data?.orders_where_prepay_closed?.length > 0) {
          const eventName = data.orders_where_prepay_closed[0]?.event_name;
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>There was an issue with your submission &mdash; prepay orders are closed for <span style="font-style: italic;">${eventName}</span></p>
              <p>Please remove items for this event from your cart and try again.</p>
              <p>You can place an order for <span style="font-style: italic;">${eventName}</span> as soon as your student's image is ready.</p>
            `,
          });
        }

        if (data?.error_identifier === 'phase_mismatch') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });
          const firstMismatch = data.phase_mismatch_orders[0];
          return Swal.fire({
            icon: 'warning',
            title: 'Offers Have Changed',
            html: `
              <p>The offers for <span style="font-style: italic;">${escapeHtml(
                firstMismatch.event_name,
              )}</span> have changed since you added items to your cart.</p>
              <p>Please remove items for this event from your cart and add them again to see the current pricing and available options.</p>
              <p>We apologize for any inconvenience this may cause.</p>
            `,
          });
        }

        if (data?.error_identifier === 'cart_already_has_order') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>There was an issue with your submission: you've previously placed an order for your current cart.</p>
              <p>This situation can occur if your cart wasn't fully cleared after a previous order. Please clear your cart completely and retry your order.</p>
            `,
          });
        }

        if (data?.error_identifier === 'unresolvable_cart_items') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>There was an issue with your submission: one or more items in your cart are no longer available.</p>
              <p>Please remove the affected items (or clear your cart) and retry your order. If you run into this issue again, please contact customer support.</p>
            `,
          });
        }

        if (data?.error_identifier === 'missing_incentives') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>There was an issue with your submission: some of the incentive products you qualified for are missing from your cart.</p>
              <p>Please clear your cart completely and retry your order. If you run into this issue again, please contact customer support.</p>
            `,
          });
        }

        if (data?.error_identifier === 'orders_with_multiple_price_lists') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>There was an issue with your submission: one of your student's orders contains items from multiple events.</p>
              <p>Please clear your cart and retry your order. Please only place orders for one event per student. If you run into this issue again, please contact customer support.</p>
            `,
          });
        }

        if (data?.error_identifier === 'missing_personalization') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            html: `
              <p>One or more products in your cart require personalization.</p>
              <p>Please go back and enter a personalization value for all required products before submitting.</p>
            `,
          });
        }

        // A package with no items chosen (ENG-1683).
        if (data?.error_identifier === 'unconfigured_bundles') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire(
            unconfiguredBundlesAlert(data?.unconfigured_bundles, orders),
          );
        }

        if (data?.error_identifier === 'missing_required_product_options') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            title: 'Product Options Need Attention',
            html: missingRequiredProductOptionsHtml(
              data?.missing_required_product_options,
            ),
          });
        }

        if (data?.error_identifier === 'missing_retouching_selections') {
          dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

          return Swal.fire({
            icon: 'warning',
            title: 'Retouching Needs Attention',
            text: `${
              data?.message ||
              'Your retouching purchase needs to know which images to retouch.'
            } Please edit the retouching item in your cart and review your image selections.`,
          });
        }
        // Any other 422: the server refused for a reason this ladder has no
        // arm for. Fall through to the generic dispatch and the parent sees
        // nothing at all — the spinner stops and "Place Order" reads as dead —
        // because the reducer discards V3_SUBMIT_CART_ERROR's payload and no
        // component renders it. So say what the server said, generically
        // (ENG-2043 review F3). Every arm above returns, so reaching here
        // means nothing handled it.
        dispatch({ type: V3_SUBMIT_CART_ERROR, payload: data?.message });

        return Swal.fire({
          icon: 'warning',
          html: `
              <p>${escapeHtml(
                data?.message || 'There was an issue with your submission.',
              )}</p>
              <p>Please review your cart and try again. If the problem persists, contact customer support.</p>
            `,
        });
      }

      // General error handling
      dispatch({
        type: V3_SUBMIT_CART_ERROR,
        payload: error.message || 'An unexpected error occurred.',
      });
    }
  };

export const submitCartSuccess = () => ({
  type: V3_SUBMIT_CART_SUCCESS,
});

export const storeSuccessAndBuildOrder =
  (sessionId, uuid, retouchingAttemptId = null) =>
  async (dispatch, getState) => {
    try {
      const result = await axios.post(
        '/api/v3/orders/store-success-build-order',
        {
          sessionId,
          uuid,
          ...(retouchingAttemptId ? { retouchingAttemptId } : {}),
        },
      );

      if (result?.status === 200 && result.data?.session_id_matches === true) {
        if (getState()?.v3Order?.uuid === uuid)
          dispatch({ type: V3_CLEAR_CART, payload: { verifiedUuid: uuid } });

        // Clear the campaign event ID cookie
        Cookies.remove('campaign_event_id');
        return true;
      } else {
        Sentry.captureMessage(
          `store-success-build-order returned ${result?.status} (cart_uuid: ${uuid})`,
        );
      }
    } catch (error) {
      // The cron rescue will still build this order, but the receipt email
      // is delayed until it runs — track how often that happens.
      Sentry.captureException(error, {
        extra: { context: 'store-success-build-order', cart_uuid: uuid },
      });
    }
    return false;
  };

export const cancelRetouchingCheckout =
  (uuid, retouchingAttemptId) => async (dispatch, getState) => {
    if (!uuid || !retouchingAttemptId || getState().v3Order.uuid !== uuid) {
      await showProtectedCheckout(
        'The checkout return does not match this cart. Your cart has been preserved. Please contact support.',
      );
      return false;
    }
    if (
      getState().v3Order.retouchingAttemptId &&
      getState().v3Order.retouchingAttemptId !== retouchingAttemptId
    ) {
      await showProtectedCheckout(
        'This return belongs to an older checkout. The current checkout remains protected.',
      );
      return false;
    }
    dispatch({
      type: 'V3_RETOUCHING_CHECKOUT_PENDING',
      payload: { attemptId: retouchingAttemptId },
    });
    try {
      const result = await axios.post(
        '/api/v3/orders/cancel-retouching-checkout',
        { uuid, retouchingAttemptId },
      );
      if (
        result.status === 200 &&
        result.data?.requiresReview === true &&
        result.data.retiredRetouchingAttemptId === retouchingAttemptId &&
        getState().v3Order.uuid === uuid
      ) {
        return retireReviewedCheckout(
          dispatch,
          getState,
          uuid,
          retouchingAttemptId,
        );
      }
    } catch (error) {
      await showProtectedCheckout(
        error?.response?.data?.message ||
          'Checkout could not be cancelled. Your cart remains protected. Please contact support.',
      );
    }
    return false;
  };

export const clearCart = () => (dispatch, getState) => {
  const { uuid } = getState().v3Order;

  // Clean up Pics+ localStorage entries for this cart
  if (uuid) {
    localStorage.removeItem(`picsPlusModalShown_${uuid}`);
    localStorage.removeItem(`picsPlusAccepted_${uuid}`);
  }

  dispatch({ type: V3_CLEAR_CART });
};

// `recordDecline: false` removes the row without recording an optional
// incentive decline — for removals the app performs on the parent's behalf
// (the cart's restricted-shipping cleanup), which are not a choice the parent
// made (ENG-2043 R6).
export const removeCartItem = (
  cartItemId,
  orderKey,
  { recordDecline = true } = {},
) => {
  return {
    type: V3_REMOVE_CART_ITEM,
    payload: { cartItemId, orderKey, recordDecline },
  };
};

export const removeCartItems = (cartItemIds, orderKey) => (dispatch) => {
  cartItemIds.forEach((cartItemId) => {
    dispatch(removeCartItem(cartItemId, orderKey));
  });
};

// Edit one retouching line's image selection in place, scoped to the order it
// belongs to. Rebuilding the line through addToCart would create a new variant
// and trigger the normal product-routing middleware instead of preserving the
// cart edit in place.
// notes: the line's per-photo notes (pose id -> text); omitted keeps the
// line's current notes.
// Optional fields are named, so each caller passes only what it changes.
// optionFields: the line's product option fields
// (cartUtils.productOptionCartFields); omitted, the line keeps its own.
// sources: which UI added each photo (pose id -> source). First touch wins:
// the reducer keeps an existing entry and only fills photos that have none.
export const updateRetouchingSelection = (
  orderKey,
  cartItemId,
  poses,
  unitPrice,
  notes,
  { optionFields, sources } = {},
) => ({
  type: V3_UPDATE_RETOUCHING_SELECTION,
  payload: {
    orderKey,
    cartItemId,
    poses,
    unitPrice,
    notes,
    optionFields,
    sources,
  },
});

// Edit-mode line clearing is intentionally separate from the general remove
// action. General removal eagerly prunes incentives before the order/cart
// scope and voucher total can be reconciled by finishRetouchingEdit.
export const clearRetouchingSelection = (cartItemId, orderKey) => ({
  type: V3_CLEAR_RETOUCHING_SELECTION,
  payload: { cartItemId, orderKey },
});

// Finish an order-scoped retouching edit only after all line updates/removals
// have reached the reducer. Incentives use the order's snapshot, with the live
// catalog only when Redux is currently loaded for that same event.
// stayOnCart: the edit was made on the cart itself (the chip), so it does not
// push another /v3/cart entry; an owed bonus still opens its configurator.
export const finishRetouchingEdit =
  (orderKey, { stayOnCart = false } = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const orders = state.v3Order?.orders || {};
    const voucherTotal = state.cartVoucherItems?.voucherSubtotalCents || 0;
    const shootEventId = state.shoot?.shoot?.event_id || state.shoot?.shoot?.id;

    Object.entries(orders).forEach(([candidateOrderKey, order]) => {
      const catalog = order?.productContext?.incentiveProducts || [];
      const candidateEventId =
        order?.event?.id ?? candidateOrderKey.split('-')[0];
      const currentEventCatalog =
        shootEventId != null &&
        String(candidateEventId) === String(shootEventId)
          ? state.shoot?.incentiveProducts || []
          : [];

      (order?.cartItems || [])
        .filter((item) => item.isIncentive)
        .forEach((item) => {
          const definition =
            catalog.find((product) => product.id === item.productId) ||
            currentEventCatalog.find(
              (product) => product.id === item.productId,
            );
          const threshold =
            item.incentiveThreshold ?? definition?.incentive_threshold;
          const appliesTo =
            item.incentiveAppliesTo ?? definition?.incentive_applies_to;

          // A legacy line with no threshold or scope metadata cannot be
          // classified safely, so retain it rather than silently taking away a
          // bonus that may apply to the whole cart.
          if (threshold == null || appliesTo == null) return;

          const qualifyingTotal = cartUtils.qualifyingIncentiveSpend({
            orders,
            orderKey: candidateOrderKey,
            incentiveProductId: item.productId,
            appliesTo,
            voucherTotalCents: voucherTotal,
          });
          if (qualifyingTotal < threshold) {
            dispatch(
              removeCartItem(item.id, candidateOrderKey, {
                recordDecline: false,
              }),
            );
          }
        });
    });

    const refreshedState = getState();
    const refreshedOrders = refreshedState.v3Order?.orders || {};
    const targetOrder = refreshedOrders[orderKey];
    if (
      targetOrder &&
      selectIncentiveConfiguration(refreshedState, orderKey).product
    ) {
      dispatch(
        push(`/v3/incentives/config?orderKey=${encodeURIComponent(orderKey)}`),
      );
      return;
    }

    if (!stayOnCart) dispatch(push('/v3/cart'));
  };

export const updateSMSNumberConfirmed = (value) => ({
  type: 'V3_UPDATE_SMS_NUMBER_CONFIRMED',
  payload: value,
});

export const updateParentSMSNumber = (id, phoneNumber) => {
  const data = { sms_phone: phoneNumber };

  return async (dispatch) => {
    try {
      const response = await axios.patch(`/api/v2/parents/${id}`, data);
      if (response.status >= 200 && response.status < 300) {
        dispatch({ type: 'V3_UPDATE_SMS_NUMBER_CONFIRMED', payload: true });
        dispatch(setSmsPhone(phoneNumber));
      } else {
        throw new Error('Something went wrong'); // throw an error if the response is not 2xx
      }
    } catch (error) {
      Swal.fire('Something went wrong', error.toString(), 'error'); // show the error to the user
    }
  };
};

export const declineInterstitialProduct = (orderKey, productId) => ({
  type: V3_DECLINE_INTERSTITIAL_PRODUCT,
  payload: { orderKey, productId },
});

export const declinePreCartOfferProduct = (
  orderKey,
  productId,
  redirectSource = null,
) => ({
  type: V3_DECLINE_PRE_CART_OFFER_PRODUCT,
  payload: { productId, redirectSource, orderKey },
});

export const checkAndApplyDisountCode =
  (discountCode) => async (dispatch, getState) => {
    dispatch(checkoutIsLoading(true));

    const { pricing_model } = getState()?.shoot?.shoot || {};
    const dataToSubmit = {
      code: discountCode,
      company_pricing_model_id: pricing_model?.id,
    };

    if (!dataToSubmit.code) {
      dispatch(checkoutIsLoading(false));
      return Swal.fire('Error', 'You must submit a code', 'error');
    }

    try {
      const response = await axios.get(
        `/api/v2/discount-codes/apply-discount-code`,
        {
          params: dataToSubmit,
        },
      );

      const { data } = response;

      if (data) {
        if (!data.valid) {
          return Swal.fire('Something went wrong', data.message, 'warning');
        }

        dispatch(applyDiscountCodeToCart(data));
        dispatch(storeCart());
      } else {
        return Swal.fire(
          'Something went wrong',
          'Discount Code not found',
          'error',
        );
      }
    } catch (error) {
      return Swal.fire('Something went wrong', error.toString(), 'error');
    } finally {
      dispatch(checkoutIsLoading(false));
    }
  };

export const checkoutIsLoading = (checkoutIsLoading) => ({
  type: V3_CHECKOUT_LOADING,
  payload: { checkoutIsLoading },
});

export const applyDiscountCodeToCart = (data) => ({
  type: V3_APPLY_DISCOUNT_CODE_TO_CART,
  payload: { data },
});

// Replaces the device cart with the saved cart an abandoned-cart reminder link
// names. Resolves true once the cart is installed, false when the request was
// superseded; rejects when the server refuses the restore.
//
// The link names only the reminder and the server finds the cart from it. A
// cart uuid carried by an older link is never sent, but must match the reply.
export const restoreAbandonedCartAttribution =
  ({ cartUuid, abandonedCartNotificationId }, isCurrent = () => true, signal) =>
  async (dispatch, getState) => {
    const refuseProtectedRestore = () => {
      const current = getState().v3Order;
      if (
        current.retouchingCheckoutProtected ||
        current.retouchingCheckoutRequest
      )
        throw new Error(
          'The current checkout is protected; cancel it before restoring a cart',
        );
    };
    refuseProtectedRestore();
    const requestId = {};
    cartRecoveryRequests.set(getState, requestId);
    try {
      const response = await axios.get('/api/v3/orders/restore-cart', {
        params: { abandoned_cart_notification_id: abandonedCartNotificationId },
        signal,
      });
      if (!isCurrent() || cartRecoveryRequests.get(getState) !== requestId)
        return false;
      const payload = response.data;
      if (
        !payload?.orders ||
        !payload.uuid ||
        (cartUuid && payload.uuid !== cartUuid) ||
        String(payload.abandonedCartNotificationId) !==
          String(abandonedCartNotificationId)
      ) {
        throw new Error('Invalid saved cart response');
      }
      refuseProtectedRestore();
      // The saved cart is installed after rehydration, so migration 10 never
      // saw it: stamp its poses' image record types the same way, from each
      // line's imageNodes or the catalog loaded now (the restore clears it),
      // then any pose that leaves unstamped from the server's list of the
      // order's group photos, so the retouching picker and chip never offer a
      // group photo.
      const stamped = stampPoseImageRecordTypes({
        shoot: getState().shoot,
        v3Order: { orders: payload.orders },
      });
      dispatch({
        type: V3_RESTORE_ABANDONED_CART_ATTRIBUTION,
        payload: {
          ...payload,
          orders: stampPosesFromGroupPhotoIds(
            stamped.v3Order.orders,
            payload.groupPhotoIds,
          ),
        },
      });
      if (getState().v3Order.uuid !== payload.uuid)
        throw new Error(
          'The current checkout is protected; cancel it before restoring another cart',
        );
      return true;
    } finally {
      if (cartRecoveryRequests.get(getState) === requestId)
        cartRecoveryRequests.delete(getState);
    }
  };

export const resetSubmittingOrder = () => {
  return {
    type: V3_RESET_SUBMITTING_ORDER,
  };
};

export const confirmShippingAddress = () => {
  return {
    type: V3_CONFIRM_SHIPPING_ADDRESS,
  };
};

export const setAcceptedPicsPlus = (accepted) => {
  return {
    type: V3_SET_ACCEPTED_PICS_PLUS,
    payload: { accepted },
  };
};

export const logPicsPlusInteraction =
  (eventType, triggeredFrom = null, metadata = {}) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const cartUuid = state.v3Order.uuid;
      const picsPlusExperiment = state.v3Order.picsPlusExperiment;

      if (!cartUuid) {
        console.warn('Cannot log Pics+ interaction: cart UUID not available');
        return;
      }

      // Include experiment context in metadata
      const enrichedMetadata = {
        ...metadata,
        experiment_id: picsPlusExperiment?.experiment_id || null,
        experiment_name: picsPlusExperiment?.experiment_name || null,
        variant_id: picsPlusExperiment?.variant_id || null,
        variant_name: picsPlusExperiment?.variant_name || null,
        price_cents: picsPlusExperiment?.price_cents || null,
      };

      const payload = {
        cart_uuid: cartUuid,
        event_type: eventType,
        triggered_from: triggeredFrom,
        metadata: enrichedMetadata,
      };

      await axios.post('/api/v3/pics-plus-interactions', payload);
    } catch (error) {
      console.error('Error logging Pics+ interaction:', error);
    }
  };
